/**
 * A tela de Prazos: o que vence primeiro e a receita em risco (`C-15`, ADR-068).
 *
 * Três relógios correm contra a arrecadação de cada passagem — o envio à SENATRAN (24 h da
 * passagem), o processamento na SENATRAN (7 dias) e o pagamento (o limite do débito) —, e a
 * pergunta desta tela é a de quem cuida do dinheiro: **o que vence primeiro, quanto vale e
 * quanto se perde se vencer.**
 *
 * - **A tela não calcula nada.** O que corre em cada trilha, o aviso, o vencimento e a perda
 *   vêm do servidor, pelas regras do domínio e pelo regime de cada concessão — no paulista, a
 *   passagem identificada que vence volta a 95% pela compensação; no nacional, perde-se tudo.
 * - **Todo número abre a fila que o produziu** (`AC14`): a SENATRAN com o recorte, a mesma
 *   contagem.
 * - **A fila de urgência** junta as três trilhas pela ordem do vencimento, e a passagem
 *   crítica — a menos de 4 horas do envio, a menos de um dia do cancelamento ou do limite —
 *   vem em vermelho e marcada. Na aba de uma trilha (e no clique do aviso, que abre a
 *   SENATRAN pelo vencimento), a crítica é a primeira.
 * - **A tela relê a cada minuto** — o servidor guarda a conta até o fim do minuto do relógio
 *   da organização —, e a resposta que chega depois de uma troca de filtro é descartada.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router";

import { ErroDaApi, api } from "../api/cliente";
import { APRESENTACAO_DO_STATUS } from "../api/tipos";
import { useSessao } from "../api/contexto-sessao";
import type {
  ContadorDePrazos,
  FilaDePrazos,
  PrazoNaFila,
  ProtocoloNaReceita,
  ReceitaEmRisco,
  TrilhaDePrazoNaTela,
} from "../api/tipos";
import { Badge, Botao, Estado, Kpi, Painel } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { useAgora } from "../ganchos/useAgora";
import { useCelular } from "../ganchos/useCelular";
import { supervisiona } from "../componentes/navegacao";
import { ImportacaoDeNotificacoes } from "./ImportacaoDeNotificacoes";
import { dataEHora, numeroLongo, reais, restante } from "../formato";
import { useDeslocamento } from "../relogio";
import { ABAS_DA_FILA, DURACAO_DA_TRILHA, enderecoDaFila } from "./prazos";
import { repetirComAbaVisivel } from "../ganchos/repetirComAbaVisivel";

const POR_PAGINA = 25;

/** De quanto em quanto a tela relê: o minuto que o servidor guarda. */
export const RELEITURA_MS = 60_000;

type Trilha = (typeof ABAS_DA_FILA)[number]["trilha"];

function mensagem(falha: unknown, padrao: string): string {
  return falha instanceof ErroDaApi ? falha.message : padrao;
}

function valorOuSemTarifa(
  centavos: number | null | undefined,
  quantidade: number,
): string {
  if (quantidade === 0) return reais(0);
  return centavos == null ? "sem tarifa" : reais(centavos);
}

export function Prazos() {
  const [concessao, definirConcessao] = useState("");
  const [visao, definirVisao] = useState<ReceitaEmRisco | null>(null);
  // `semLicenca` separa o 402 da falha de rede (`N253`): a releitura que falha não derruba a tela
  // inteira (a última leitura que deu certo continua), mas a licença que acabou derruba.
  const [erro, definirErro] = useState<{
    mensagem: string;
    semLicenca: boolean;
  } | null>(null);
  const [recalculando, definirRecalculando] = useState(false);
  const [tentativa, definirTentativa] = useState(0);
  const navegar = useNavigate();
  // O relógio da organização mudou (o avanço da demonstração): a conta é outra (ADR-059).
  const deslocamento = useDeslocamento();

  // A última leitura pedida é a única que vale: a do "atualizar" de uma concessão que chega
  // depois da troca para outra é descartada.
  const ultima = useRef(0);
  const carregar = useCallback(
    async (fresco: boolean) => {
      const esta = ++ultima.current;
      const valida = () => esta === ultima.current;
      if (fresco) definirRecalculando(true);
      try {
        const p = new URLSearchParams();
        if (concessao) p.set("concessao_id", concessao);
        if (fresco) p.set("fresco", "true");
        const lida = await api<ReceitaEmRisco>(
          `/tollvision/prazos/receita-em-risco?${p.toString()}`,
        );
        if (!valida()) return;
        definirVisao(lida);
        definirErro(null);
      } catch (falha) {
        if (valida())
          definirErro({
            mensagem: mensagem(falha, "Não foi possível ler os prazos."),
            semLicenca: falha instanceof ErroDaApi && falha.semLicenca,
          });
      } finally {
        if (valida()) definirRecalculando(false);
      }
    },
    [concessao],
  );

  useEffect(() => {
    void deslocamento;
    void tentativa;
    void carregar(false);
    const parar = repetirComAbaVisivel(
      () => void carregar(false),
      RELEITURA_MS,
    );
    return () => {
      ultima.current++;
      parar();
    };
  }, [carregar, deslocamento, tentativa]);

  function abrir(recorte: string) {
    navegar(enderecoDaFila(recorte, concessao));
  }

  const contador = (chave: string): ContadorDePrazos | undefined =>
    visao?.contadores.find((c) => c.chave === chave);
  const calculadoAs = visao
    ? new Date(visao.calculado_em).toLocaleTimeString("pt-BR", {
        timeZone: "America/Sao_Paulo",
        hour: "2-digit",
        minute: "2-digit",
      })
    : null;

  return (
    <>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <h1 className="text-titulo-tela leading-none font-extrabold tracking-[-0.01em] text-tinta uppercase">
            Prazos e receita em risco
          </h1>
          <p className="text-subtitulo-tela text-tinta-3">
            O que vence primeiro, quanto vale e quanto se perde se vencer
          </p>
        </div>
        <div className="ml-auto flex min-w-0 max-w-full flex-wrap items-center gap-2">
          <select
            aria-label="Concessão"
            value={concessao}
            onChange={(e) => definirConcessao(e.target.value)}
            className="min-h-8 max-w-full min-w-0 rounded-botao border border-linha bg-painel-2 px-2 text-[11.5px] text-tinta"
          >
            <option value="">Todas as concessões</option>
            {visao?.concessoes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nome}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={recalculando || !calculadoAs}
            onClick={() => void carregar(true)}
            title="Conta de novo agora, sem o minuto guardado."
            className="inline-flex items-center gap-1.5 rounded-pilula border border-linha bg-painel-2 px-2.5 py-1 text-[11px] text-tinta-3 hover:text-tinta-2 disabled:opacity-70"
          >
            <Icone.Recarga />
            {recalculando
              ? "Recalculando…"
              : calculadoAs
                ? `Calculado às ${calculadoAs} · atualizar`
                : "Carregando…"}
          </button>
        </div>
      </div>

      {erro && (!visao || erro.semLicenca) ? (
        <Painel titulo="Prazos">
          <Estado
            tipo={erro.semLicenca ? "sem-licenca" : "erro"}
            titulo={
              erro.semLicenca
                ? "Esta organização não licenciou o módulo TollVision."
                : erro.mensagem
            }
            acao={
              <Botao onClick={() => definirTentativa((n) => n + 1)}>
                Tentar de novo
              </Botao>
            }
          />
        </Painel>
      ) : !visao ? (
        <Painel titulo="Prazos">
          <Estado tipo="carregando" titulo="Contando os prazos…" />
        </Painel>
      ) : (
        <div className="grid min-h-0 auto-rows-max content-start gap-3 overflow-y-auto pb-1">
          {/* A releitura de minuto falhou: os números abaixo são os da última que deu certo,
              e a tela diz isso em vez de trocar tudo por um erro até a próxima. */}
          {erro && (
            <p
              role="alert"
              className="flex flex-wrap items-center gap-2 rounded-botao border border-vermelho-borda bg-vermelho-fundo px-2.5 py-2 text-[11.5px] text-vermelho"
            >
              <span className="min-w-0 flex-1 wrap-anywhere">
                {erro.mensagem} Os números abaixo são da leitura das{" "}
                {calculadoAs}.
              </span>
              <Botao onClick={() => definirTentativa((n) => n + 1)}>
                Tentar de novo
              </Botao>
            </p>
          )}
          <section
            aria-label="Contadores"
            className="grid grid-cols-2 gap-2.5 xl:grid-cols-4"
          >
            <ReceitaEmRiscoNoTopo
              contador={contador("em_risco")}
              aoAbrir={abrir}
            />
            {(
              [
                ["vencem_em_24h", <Icone.Relogio key="r" />],
                ["venceram_hoje", <Icone.Alerta key="a" />],
                ["cancelados_hoje", <Icone.Alerta key="c" />],
              ] as const
            ).map(([chave, icone]) => {
              const c = contador(chave);
              return c ? (
                <Kpi
                  key={chave}
                  rotulo={c.rotulo}
                  valor={c.quantidade}
                  icone={icone}
                  severidade="critico"
                  detalhe={valorOuSemTarifa(c.valor_centavos, c.quantidade)}
                  aoClicar={() => abrir(c.recorte)}
                />
              ) : null;
            })}
          </section>

          <section
            aria-label="As três trilhas"
            className="grid grid-cols-1 gap-3 lg:grid-cols-3"
          >
            {visao.trilhas.map((t) => (
              <CartaoDaTrilha key={t.tipo} trilha={t} concessao={concessao} />
            ))}
          </section>

          {visao.protocolo && (
            <ProvaDaNotificacaoNosPrazos
              protocolo={visao.protocolo}
              aoAbrir={abrir}
              aoGravar={() => void carregar(true)}
            />
          )}

          <FilaDeUrgencia
            concessao={concessao}
            visao={visao}
            gatilho={`${visao.calculado_em}`}
          />

          {visao.regimes.length > 0 && (
            <section
              aria-label="Como a perda é contada"
              className="grid gap-1 rounded-cartao border border-linha bg-painel-2 px-3 py-2 text-[11px] text-tinta-3"
            >
              <b className="text-titulo-cartao tracking-[0.07em] uppercase">
                Como a perda é contada
              </b>
              {visao.regimes.map((r) => (
                <p key={r.concessao_id}>
                  <b className="text-tinta-2">{r.concessao}</b> — {r.regra}
                </p>
              ))}
            </section>
          )}
        </div>
      )}
    </>
  );
}

/**
 * A prova do protocolo de notificação (`OB-08`, ADR-071), só quando alguma concessão do recorte
 * está num regime que a pede (o federal): as passagens cujo 8º ou 15º dia é hoje sem a
 * notificação registrada, e as que passaram do dia sem ela — com a parte do poder concedente
 * que se perde sem a prova. Os dois números abrem a SENATRAN com a mesma contagem (`AC14`). A
 * supervisão importa a planilha do provedor daqui (*Importar planilha*, a ação do painel).
 */
function ProvaDaNotificacaoNosPrazos({
  protocolo,
  aoAbrir,
  aoGravar,
}: {
  protocolo: ProtocoloNaReceita;
  aoAbrir: (recorte: string) => void;
  aoGravar: () => void;
}) {
  const { quem } = useSessao();
  const [importando, definirImportando] = useState(false);
  const { notificar_hoje: hoje, em_falta: falta } = protocolo;
  return (
    <section aria-label="Prova da notificação" className="grid gap-3">
      <Painel
        titulo="Prova da notificação"
        acoes={
          supervisiona(quem?.perfil) && (
            <Botao
              papel="primaria"
              aria-pressed={importando}
              onClick={() => definirImportando((v) => !v)}
            >
              <Icone.Subir />
              Importar planilha
            </Botao>
          )
        }
      >
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2">
          <Kpi
            rotulo={hoje.rotulo}
            valor={hoje.quantidade}
            icone={<Icone.Relogio />}
            severidade="atencao"
            detalhe={`${valorOuSemTarifa(hoje.valor_centavos, hoje.quantidade)} — a notificação precisa sair hoje`}
            aoClicar={() => aoAbrir(hoje.recorte)}
          />
          <Kpi
            rotulo={falta.rotulo}
            valor={falta.quantidade}
            icone={<Icone.Alerta />}
            severidade="critico"
            detalhe={
              falta.quantidade === 0
                ? "nenhuma passagem sem a prova nos 180 dias"
                : falta.perda_centavos == null
                  ? "sem tarifa para contar"
                  : `${reais(falta.perda_centavos)} do poder concedente se perdem sem a prova`
            }
            aoClicar={() => aoAbrir(falta.recorte)}
          />
        </div>
        {protocolo.regras.map((r) => (
          <p key={r} className="text-[10.5px] leading-snug text-tinta-3">
            {r}
          </p>
        ))}
      </Painel>
      {importando && <ImportacaoDeNotificacoes aoGravar={aoGravar} />}
    </section>
  );
}

function ReceitaEmRiscoNoTopo({
  contador,
  aoAbrir,
}: {
  contador: ContadorDePrazos | undefined;
  aoAbrir: (recorte: string) => void;
}) {
  if (!contador) return null;
  return (
    <button
      type="button"
      onClick={() => aoAbrir(contador.recorte)}
      className="col-span-2 grid content-start gap-1 rounded-cartao border border-amarelo-borda bg-amarelo-fundo px-3 py-2.5 text-left hover:bg-realce xl:col-span-1"
    >
      <span className="text-[11.5px] text-tinta-3">{contador.rotulo}</span>
      <b className="mono text-[24px] leading-none text-tinta">
        {contador.quantidade === 0
          ? reais(0)
          : contador.perda_centavos == null
            ? "sem tarifa"
            : reais(contador.perda_centavos)}
      </b>
      <span className="text-[10.5px] leading-tight text-tinta-3">
        se vencerem — {numeroLongo(contador.quantidade)} passagens,{" "}
        {valorOuSemTarifa(contador.valor_centavos, contador.quantidade)} em jogo
        {contador.sem_tarifa > 0 &&
          ` (${numeroLongo(contador.sem_tarifa)} sem tarifa)`}
      </span>
    </button>
  );
}

function CartaoDaTrilha({
  trilha: t,
  concessao,
}: {
  trilha: TrilhaDePrazoNaTela;
  concessao: string;
}) {
  const agora = useAgora(30_000);
  return (
    <section
      aria-label={`Trilha: ${t.rotulo}`}
      className="grid content-start gap-2 rounded-cartao border border-linha bg-painel p-3"
    >
      <header className="flex items-center gap-2">
        <h2 className="text-titulo-painel font-bold tracking-[0.08em] text-tinta uppercase">
          {t.rotulo}
        </h2>
        <Badge cor="cinza">{DURACAO_DA_TRILHA[t.tipo]}</Badge>
      </header>
      <div className="flex items-baseline gap-2">
        <Link
          to={enderecoDaFila(t.recorte, concessao)}
          className="mono text-[24px] leading-none font-extrabold text-tinta hover:underline"
          title="Abrir na fila"
        >
          {numeroLongo(t.abertos)}
        </Link>
        <span className="mono text-[11.5px] text-tinta-2">
          {valorOuSemTarifa(t.valor_centavos, t.abertos)}
        </span>
      </div>
      <p className="text-[11.5px] text-tinta-2">
        {t.perda_centavos == null
          ? t.abertos === 0 || t.tipo === "AUTUACAO_30D"
            ? "Não é perda: o débito segue em aberto."
            : "Perda: sem tarifa para contar."
          : `Perde ${reais(t.perda_centavos)} se vencer`}
      </p>
      <Link
        to={enderecoDaFila(t.recorte_critico, concessao)}
        className={`text-[11.5px] font-semibold hover:underline ${
          t.criticos > 0 ? "text-vermelho" : "text-tinta-3"
        }`}
      >
        {numeroLongo(t.criticos)} {t.aviso}
        {t.criticos > 0 &&
          ` · ${valorOuSemTarifa(t.valor_criticos_centavos, t.criticos)}`}
      </Link>
      <p className="text-[11px] text-tinta-3">
        {!t.proximo
          ? "Nada correndo nesta trilha."
          : new Date(t.proximo) <= agora
            ? "O primeiro acabou de vencer — a conta se atualiza no próximo minuto."
            : `Próximo vence em ${restante(t.proximo, agora)}`}
      </p>
      <p className="text-[10.5px] leading-snug text-tinta-3">
        Ao vencer: {t.consequencia}
      </p>
      {t.nota && (
        <p className="text-[10px] leading-snug text-tinta-3">{t.nota}</p>
      )}
    </section>
  );
}

function FilaDeUrgencia({
  concessao,
  visao,
  gatilho,
}: {
  concessao: string;
  visao: ReceitaEmRisco;
  gatilho: string;
}) {
  const [trilha, definirTrilha] = useState<Trilha>(null);
  const [pagina, definirPagina] = useState(0);
  const [fila, definirFila] = useState<FilaDePrazos | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const celular = useCelular();
  const agora = useAgora(30_000);

  useEffect(() => definirPagina(0), [trilha, concessao]);
  // A página que esvaziou depois de uma releitura (o relógio andou) volta para a primeira, em
  // vez de dizer "nada correndo" com itens nas anteriores.
  useEffect(() => {
    if (fila && fila.itens.length === 0 && pagina > 0) definirPagina(0);
  }, [fila, pagina]);

  useEffect(() => {
    let valida = true;
    const p = new URLSearchParams({
      pagina: String(pagina),
      limite: String(POR_PAGINA),
    });
    if (trilha) p.set("trilha", trilha);
    if (concessao) p.set("concessao_id", concessao);
    void (async () => {
      try {
        const lida = await api<FilaDePrazos>(
          `/tollvision/prazos/fila?${p.toString()}`,
        );
        if (!valida) return;
        definirFila(lida);
        definirErro(null);
      } catch (falha) {
        if (valida)
          definirErro(mensagem(falha, "Não foi possível ler a fila."));
      }
    })();
    return () => {
      valida = false;
    };
  }, [trilha, pagina, concessao, gatilho]);

  const total = trilha
    ? (visao.trilhas.find((t) => t.tipo === trilha)?.abertos ?? 0)
    : visao.trilhas.reduce((s, t) => s + t.abertos, 0);

  return (
    <Painel
      titulo="Fila de urgência"
      rolagemPropria
      acoes={
        <div
          role="group"
          aria-label="Trilha da fila"
          className="flex flex-wrap gap-1"
        >
          {ABAS_DA_FILA.map((aba) => (
            <button
              key={aba.rotulo}
              type="button"
              aria-pressed={trilha === aba.trilha}
              onClick={() => definirTrilha(aba.trilha)}
              className={`rounded-pilula border px-2 py-0.5 text-[11px] font-semibold ${
                trilha === aba.trilha
                  ? "border-azul-cheio bg-azul-cheio text-white"
                  : "border-linha bg-painel-2 text-tinta-2 hover:bg-realce"
              }`}
            >
              {aba.rotulo}
            </button>
          ))}
        </div>
      }
      rodape={
        <div className="flex w-full items-center gap-2">
          <span>
            {numeroLongo(total)} correndo · o que vence primeiro vem primeiro
          </span>
          <span className="ml-auto flex gap-1">
            <Botao
              disabled={pagina === 0}
              onClick={() => definirPagina((n) => Math.max(0, n - 1))}
            >
              Anterior
            </Botao>
            <Botao
              disabled={!fila?.tem_mais}
              onClick={() => definirPagina((n) => n + 1)}
            >
              Próxima
            </Botao>
          </span>
        </div>
      }
    >
      {erro ? (
        <Estado tipo="erro" titulo={erro} />
      ) : !fila ? (
        <Estado tipo="carregando" titulo="Lendo a fila…" />
      ) : fila.itens.length === 0 ? (
        <Estado
          tipo="vazio"
          titulo="Nada correndo contra prazo aqui."
          detalhe="Quando uma passagem entrar numa trilha, ela aparece nesta fila pela ordem do vencimento."
        />
      ) : celular ? (
        <ul aria-label="Fila de urgência" className="grid gap-2">
          {fila.itens.map((item) => (
            <li key={item.transito_id}>
              <ItemNoCelular item={item} concessao={concessao} agora={agora} />
            </li>
          ))}
        </ul>
      ) : (
        <div className="min-h-0 overflow-auto">
          <table className="w-full min-w-[860px] border-collapse text-[11.5px]">
            <thead className="sticky top-0 bg-painel-2 text-left text-rotulo tracking-[0.06em] text-tinta-3 uppercase">
              <tr>
                <th className="px-2 py-1.5">Vence em</th>
                <th className="px-2 py-1.5">Trilha</th>
                <th className="px-2 py-1.5">Placa</th>
                <th className="px-2 py-1.5">Praça</th>
                <th className="px-2 py-1.5">Situação</th>
                <th className="px-2 py-1.5 text-right">Tarifa</th>
                <th className="px-2 py-1.5 text-right">Perde se vencer</th>
                <th className="px-2 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {fila.itens.map((item) => (
                <LinhaDaFila
                  key={item.transito_id}
                  item={item}
                  concessao={concessao}
                  agora={agora}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </Painel>
  );
}

function perdaDoItem(item: PrazoNaFila): string {
  if (item.tipo === "AUTUACAO_30D") return "não é perda";
  return item.perda_centavos == null
    ? "sem tarifa"
    : reais(item.perda_centavos);
}

/** Venceu depois da última leitura: a próxima a tira da fila. Até lá, não é mais aviso. */
function venceu(item: PrazoNaFila, agora: Date): boolean {
  return new Date(item.vence_em) <= agora;
}

function LinhaDaFila({
  item,
  concessao,
  agora,
}: {
  item: PrazoNaFila;
  concessao: string;
  agora: Date;
}) {
  const status = APRESENTACAO_DO_STATUS[item.status];
  const critico = item.critico && !venceu(item, agora);
  return (
    <tr
      className={`border-b border-linha-suave ${critico ? "bg-vermelho-fundo" : ""}`}
    >
      <td
        className={`mono px-2 py-1.5 whitespace-nowrap ${
          critico ? "font-bold text-vermelho" : "text-tinta"
        }`}
        title={dataEHora(item.vence_em)}
      >
        {restante(item.vence_em, agora)}
        {critico && (
          <span className="ml-1.5">
            <Badge cor="vermelho">crítico</Badge>
          </span>
        )}
      </td>
      <td className="px-2 py-1.5 text-tinta-2">
        {DURACAO_DA_TRILHA[item.tipo]}
      </td>
      <td className="mono px-2 py-1.5 font-bold text-tinta">
        {item.placa ?? "sem placa"}
      </td>
      <td className="px-2 py-1.5 text-tinta-2">{item.praca}</td>
      <td className="px-2 py-1.5">
        <Badge cor={status.cor}>{status.rotulo}</Badge>
      </td>
      <td className="mono px-2 py-1.5 text-right text-tinta-2">
        {item.valor_centavos == null
          ? "sem tarifa"
          : reais(item.valor_centavos)}
      </td>
      <td className="mono px-2 py-1.5 text-right text-tinta">
        {perdaDoItem(item)}
      </td>
      <td className="px-2 py-1.5 text-right">
        <Link
          to={enderecoDaFila(item.recorte, concessao, item.transito_id)}
          aria-label={`Abrir ${item.placa ?? "a passagem sem placa"} na fila`}
          className="text-[11px] text-tinta-3 underline hover:text-tinta"
        >
          Abrir
        </Link>
      </td>
    </tr>
  );
}

function ItemNoCelular({
  item,
  concessao,
  agora,
}: {
  item: PrazoNaFila;
  concessao: string;
  agora: Date;
}) {
  const status = APRESENTACAO_DO_STATUS[item.status];
  const critico = item.critico && !venceu(item, agora);
  return (
    <Link
      to={enderecoDaFila(item.recorte, concessao, item.transito_id)}
      className={`grid gap-1 rounded-cartao border px-2.5 py-2 ${
        critico
          ? "border-vermelho-borda bg-vermelho-fundo"
          : "border-linha bg-painel-2"
      }`}
    >
      <span className="flex items-center gap-2">
        <b
          className={`mono text-[14px] ${critico ? "text-vermelho" : "text-tinta"}`}
        >
          {restante(item.vence_em, agora)}
        </b>
        <span className="text-[11px] text-tinta-3">
          {DURACAO_DA_TRILHA[item.tipo]}
        </span>
        <span className="ml-auto">
          <Badge cor={status.cor}>{status.rotulo}</Badge>
        </span>
      </span>
      <span className="flex items-baseline gap-2 text-[11.5px]">
        <b className="mono text-tinta">{item.placa ?? "sem placa"}</b>
        <span className="text-tinta-3">{item.praca}</span>
        <span className="mono ml-auto text-tinta-2">{perdaDoItem(item)}</span>
      </span>
    </Link>
  );
}
